import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { ChatResponse, WorkflowLaunchCandidate, WorkflowLaunchPayload, WorkflowLaunchResult } from '@cypremos/shared'
import { apiFetch } from '@/lib/apiFetch'
import { generateClientId } from '@/features/req-guard/utils/ids'
import type { Message } from './ChatPanel'
import { useChatController } from './useChatController'

const CONVERSATION_ID_KEY = 'chat.conversationId'

function getOrCreateConversationId(): string {
  let id = sessionStorage.getItem(CONVERSATION_ID_KEY)
  if (!id) {
    id = generateClientId()
    sessionStorage.setItem(CONVERSATION_ID_KEY, id)
  }
  return id
}

export async function sendDocsChatTurn(
  messages: { role: 'user' | 'assistant'; content: string }[],
  currentPath?: string
): Promise<ChatResponse> {
  const response = await apiFetch('/docs-chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, currentPath, useMock: false }),
  })
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  return response.json()
}

/**
 * The "Open Requirement Audit" launch flow, shared by every chat that talks to
 * /docs-chat (main ChatPage via useDocsChat, and the floating chat on pages without
 * the wizard). Kept as one hook so the idempotent-launch/ownership semantics in
 * docs/chat/phase-1-chat-workflow-design.md have exactly one implementation.
 */
export function useRequirementAuditLaunch() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [launchCandidate, setLaunchCandidate] = useState<WorkflowLaunchCandidate | null>(null)
  const [launching, setLaunching] = useState(false)
  const [launchError, setLaunchError] = useState<string | null>(null)

  const handleIntent = useCallback((intent: { type: string }, userMessage: Message) => {
    if (intent.type === 'open_requirement_audit') {
      // Bound at render/detection time — a later click submits this candidate as-is,
      // never re-reading the input box or "the last message" at click time.
      setLaunchCandidate({
        launchRequestId: generateClientId(),
        sourceMessageId: userMessage.id,
        triggerMessage: userMessage.content,
      })
      setLaunchError(null)
    }
  }, [])

  const handleLaunch = useCallback(async () => {
    if (!launchCandidate || launching) return
    setLaunching(true)
    setLaunchError(null)

    const payload: WorkflowLaunchPayload = {
      launchRequestId: launchCandidate.launchRequestId,
      conversationId: getOrCreateConversationId(),
      launchContext: {
        triggerMessage: launchCandidate.triggerMessage,
        requirementsText: launchCandidate.requirementsText,
        additionalContext: launchCandidate.additionalContext,
      },
    }

    try {
      const response = await apiFetch('/workflow-sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const result: WorkflowLaunchResult = await response.json()
      if (result.status === 'failed') {
        setLaunchError(t('chat.launchFailed', 'Could not open the requirement audit. Please try again.'))
        return
      }
      navigate(`/auto-test?workflowSessionId=${result.workflowSessionId}`)
    } catch (error) {
      console.error('Failed to launch workflow session:', error)
      setLaunchError(t('chat.launchFailed', 'Could not open the requirement audit. Please try again.'))
    } finally {
      setLaunching(false)
    }
  }, [launchCandidate, launching, navigate, t])

  const ctaSlot = useMemo(() => {
    if (!launchCandidate) return null
    return (
      <div className="flex flex-col gap-2 mt-2">
        <button
          onClick={handleLaunch}
          disabled={launching}
          className="self-start font-label-md text-label-md bg-brand text-bg-surface px-4 py-2 rounded-full hover:bg-brand/90 transition-colors disabled:opacity-60"
        >
          {t('chat.openRequirementAudit', 'Open Requirement Audit')}
        </button>
        {launchError && <span className="text-red-500 text-sm">{launchError}</span>}
      </div>
    )
  }, [launchCandidate, launching, launchError, handleLaunch, t])

  return { handleIntent, ctaSlot }
}

/** Docs-grounded chat controller + the requirement-audit launch CTA (main ChatPage). */
export function useDocsChat(currentPath?: string) {
  const { handleIntent, ctaSlot } = useRequirementAuditLaunch()
  const controller = useChatController({
    sendTurn: (messages) => sendDocsChatTurn(messages, currentPath),
    onIntent: handleIntent,
  })
  return { controller, ctaSlot }
}
